/**
 * API Client Configuration
 * Handles X-Org-Id header injection for all requests
 */
import axios from 'axios';

// Use env var if available, otherwise derive from current hostname (works on any domain)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
export const API_BASE = `${BACKEND_URL}/api`;

// Global timeout: prevents any request from hanging indefinitely
// 60s to cover slow analytics/aggregation endpoints (analytics/overview can take 15-20s in production)
axios.defaults.timeout = 60000;

// Module-level cache (in-memory). Source of truth is localStorage keyed per user.
let activeOrgId = null;
let activeUserId = null;
let activeOrgRole = null; // 'owner' | 'member' | null

// On module load, restore from localStorage using the last known user
const _storedUserId = localStorage.getItem('active_user_id');
if (_storedUserId) {
  activeUserId = _storedUserId;
  activeOrgId = localStorage.getItem(`active_org_id:${_storedUserId}`);
  activeOrgRole = localStorage.getItem(`active_org_role:${_storedUserId}`) || null;
}

/**
 * Get the current active org role
 */
export const getActiveOrgRole = () => {
  if (activeOrgRole) return activeOrgRole;
  const uid = activeUserId || localStorage.getItem('active_user_id');
  if (uid) return localStorage.getItem(`active_org_role:${uid}`) || null;
  return null;
};

/**
 * Set the active organization ID (per-user key in localStorage)
 */
export const setActiveOrgId = (orgId, userId = null) => {
  activeOrgId = orgId;
  const uid = userId || activeUserId;
  if (orgId && uid) {
    localStorage.setItem(`active_org_id:${uid}`, orgId);
  } else if (!orgId) {
    if (uid) localStorage.removeItem(`active_org_id:${uid}`);
    localStorage.removeItem('active_org_id');
  }
  // Persist server-side for cross-device consistency (fire-and-forget)
  if (orgId) {
    const token = localStorage.getItem("auth_token");
    axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/api/orgs/set-active`,
      { org_id: orgId },
      { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    ).catch(() => {}); // intentionally silent
  }
};

/**
 * Get the current active organization ID
 */
export const getActiveOrgId = () => {
  if (activeOrgId) return activeOrgId;
  const uid = activeUserId || localStorage.getItem('active_user_id');
  if (uid) return localStorage.getItem(`active_org_id:${uid}`);
  return null;
};

/**
 * Clear all auth/org context — call on logout
 */
export const clearAuthContext = () => {
  const uid = activeUserId;
  if (uid) {
    localStorage.removeItem(`active_org_id:${uid}`);
    localStorage.removeItem(`active_org_role:${uid}`);
  }
  localStorage.removeItem('active_org_id'); // legacy
  localStorage.removeItem('active_user_id');
  localStorage.removeItem('auth_user_cache'); // clear cached user profile
  localStorage.removeItem('subscription_ok_cache'); // clear cached subscription status
  activeOrgId = null;
  activeUserId = null;
  activeOrgRole = null;
};

/**
 * Initialize organization ID from backend — always fetches fresh for the given user.
 * Call after login with the authenticated user's ID.
 * Returns { org_id, role, name } or null on error.
 */
export const initializeOrganization = async (userId = null) => {
  try {
    const storedToken = localStorage.getItem('auth_token');

    // If a new userId is provided and differs from cached, reset the cache
    if (userId && userId !== activeUserId) {
      activeOrgId = null;
      activeOrgRole = null;
      activeUserId = userId;
      localStorage.setItem('active_user_id', userId);
    }

    // Always fetch fresh from /orgs/default to get the current role
    const response = await axios.get(`${API_BASE}/orgs/default`, {
      withCredentials: true,
      headers: storedToken ? { Authorization: `Bearer ${storedToken}` } : {}
    });

    const { organization_id: orgId, role, name } = response.data;
    setActiveOrgId(orgId, userId || activeUserId);
    // Store role
    activeOrgRole = role || 'member';
    const uid = userId || activeUserId;
    if (uid) localStorage.setItem(`active_org_role:${uid}`, activeOrgRole);

    return { org_id: orgId, role: activeOrgRole, name };
  } catch (error) {
    console.error('[API] Failed to initialize organization:', error);
    return null;
  }
};

// Interceptor: inject X-Org-Id header
axios.interceptors.request.use(
  (config) => {
    const currentOrgId = getActiveOrgId();
    if (currentOrgId && !config.headers['X-Org-Id']) {
      config.headers['X-Org-Id'] = currentOrgId;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor: handle 403 org_access_denied → clear stale org and reload
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 403) {
      const detail = error.response?.data?.detail || '';
      if (detail.toLowerCase().includes('organization') || detail.toLowerCase().includes('org')) {
        console.warn('[API] Org access denied — clearing cached org_id');
        setActiveOrgId(null);
        // Force re-init on next request
        await initializeOrganization(activeUserId);
      }
    }
    if (error.response) {
      console.error('[API] Request failed:', {
        status: error.response.status,
        url: error.config?.url,
        data: error.response.data
      });
    }
    return Promise.reject(error);
  }
);

export default axios;
